import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Plus, Edit, Trash2, Megaphone, Eye, EyeOff, Calendar, Image, Settings, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import RichTextEditor from '../../../components/RichTextEditor';
import ImageUpload from '../../../components/ImageUpload';
import { createSanitizedMarkup } from '../../../utils/sanitize';

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [tickerMaxChars, setTickerMaxChars] = useState(80);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    featured_image: '',
    churches: ['karlskrona', 'karlshamn', 'olofstrom'],
    event_date: '', // Optional event date
    is_visible: true
  });

  const churchOptions = [
    { value: 'karlskrona', label: 'Karlskrona - Vår Fru av Fatima kyrka' },
    { value: 'karlshamn', label: 'Karlshamn - S:t Bonifatius kyrka' },
    { value: 'olofstrom', label: 'Olofström - S:t Antonius kyrka' }
  ];

  const loadAnnouncements = async () => {
    try {
      // Load all announcements (including hidden) for admin
      const data = await api.getAnnouncements(null, false); // false = include hidden
      setAnnouncements(data);
    } catch (error) {
      console.error('Error loading announcements:', error);
      toast.error('Kunde inte ladda meddelanden');
    }
  };

  const loadTickerSettings = async () => {
    try {
      const settings = await api.getTickerSettings();
      setTickerMaxChars(settings.ticker_max_chars || 80);
    } catch (error) {
      console.error('Error loading ticker settings:', error);
    }
  };

  useEffect(() => {
    loadAnnouncements();
    loadTickerSettings();
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        // Only include event_date if it's set
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null
      };

      if (editingAnnouncement) {
        await api.updateAnnouncement(editingAnnouncement.id, submitData);
        toast.success('Meddelande uppdaterat!');
      } else {
        await api.createAnnouncement(submitData);
        toast.success('Meddelande skapat!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Ett fel uppstod');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill radera detta meddelande?')) {
      try {
        await api.deleteAnnouncement(id);
        toast.success('Meddelande raderat!');
        loadAnnouncements();
      } catch (error) {
        toast.error('Kunde inte radera meddelandet');
      }
    }
  };

  const handleToggleVisibility = async (announcement) => {
    try {
      await api.updateAnnouncement(announcement.id, {
        ...announcement,
        is_visible: !announcement.is_visible
      });
      toast.success(announcement.is_visible ? 'Meddelande dolt' : 'Meddelande synligt');
      loadAnnouncements();
    } catch (error) {
      toast.error('Kunde inte uppdatera synlighet');
    }
  };

  const handleSaveTickerSettings = async () => {
    try {
      await api.updateTickerSettings(tickerMaxChars);
      toast.success('Ticker-inställningar sparade!');
      setIsSettingsOpen(false);
    } catch (error) {
      toast.error('Kunde inte spara inställningar');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      featured_image: '',
      churches: ['karlskrona', 'karlshamn', 'olofstrom'],
      event_date: '',
      is_visible: true
    });
    setEditingAnnouncement(null);
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      featured_image: announcement.featured_image || '',
      churches: announcement.churches || ['karlskrona', 'karlshamn', 'olofstrom'],
      event_date: announcement.event_date ? announcement.event_date.split('T')[0] : '',
      is_visible: announcement.is_visible !== false
    });
    setIsDialogOpen(true);
  };

  const handleChurchToggle = (churchValue) => {
    setFormData(prev => {
      const churches = prev.churches.includes(churchValue)
        ? prev.churches.filter(c => c !== churchValue)
        : [...prev.churches, churchValue];
      return { ...prev, churches };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const getChurchLabels = (churches) => {
    if (!churches || churches.length === 0) return 'Ingen kyrka vald';
    if (churches.length === 3) return 'Alla kyrkor';
    return churches.map(c => {
      const option = churchOptions.find(o => o.value === c);
      return option ? option.label.split(' - ')[0] : c;
    }).join(', ');
  };

  // Filter and sort announcements
  const filteredAnnouncements = announcements
    .filter(a => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        a.title?.toLowerCase().includes(query) ||
        a.content?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-8 h-8 text-yellow-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Meddelanden</h2>
            <p className="text-gray-600">Hantera meddelanden som visas på hemsidan</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsSettingsOpen(true)}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Ticker-inställningar
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-gray-900"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nytt meddelande
          </Button>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Sök meddelanden..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="announcements-search"
          />
        </div>
        <Button
          variant="outline"
          onClick={toggleSortOrder}
          className="flex items-center gap-2"
          data-testid="announcements-sort"
        >
          {sortOrder === 'desc' ? (
            <>
              <ArrowDown className="w-4 h-4" />
              Nyast först
            </>
          ) : (
            <>
              <ArrowUp className="w-4 h-4" />
              Äldst först
            </>
          )}
        </Button>
        <span className="text-sm text-gray-500">
          {filteredAnnouncements.length} meddelanden
        </span>
      </div>

      {/* Announcements List */}
      <div className="grid gap-4">
        {filteredAnnouncements.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center text-gray-500">
              <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? 'Inga meddelanden matchade din sökning.' : 'Inga meddelanden ännu. Skapa ditt första meddelande!'}</p>
            </CardContent>
          </Card>
        ) : (
          filteredAnnouncements.map((announcement) => (
            <Card key={announcement.id} className={`${!announcement.is_visible ? 'opacity-60 bg-gray-50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Featured Image */}
                  {announcement.featured_image && (
                    <img
                      src={announcement.featured_image}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{announcement.title}</h3>
                      {!announcement.is_visible && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Dold</span>
                      )}
                      {announcement.event_date && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Händelse
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2" dangerouslySetInnerHTML={createSanitizedMarkup(announcement.content)} />
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1" title="Skapad">
                        Skapad: {formatDate(announcement.created_at)}
                      </span>
                      {announcement.event_date && (
                        <span className="flex items-center gap-1 text-yellow-700 font-medium" title="Händelsedatum">
                          <Calendar className="w-3 h-3" />
                          Händelse: {formatDate(announcement.event_date)}
                        </span>
                      )}
                      <span>{getChurchLabels(announcement.churches)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleVisibility(announcement)}
                      title={announcement.is_visible ? 'Dölj' : 'Visa'}
                    >
                      {announcement.is_visible ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(announcement)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(announcement.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Redigera meddelande' : 'Skapa nytt meddelande'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titel (Svenska) *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Meddelandets titel..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">Översätts automatiskt till andra språk</p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Innehåll (Svenska) *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                placeholder="Skriv meddelandets innehåll här..."
                minHeight="150px"
              />
              <p className="text-xs text-gray-500 mt-1">Använd verktygsfältet för att formatera text. Översätts automatiskt.</p>
            </div>

            {/* Featured Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Image className="w-4 h-4 inline mr-1" />
                Utvald bild
              </label>
              <ImageUpload
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                aspectRatio="video"
                placeholder="Klicka eller dra för att ladda upp bild"
              />
              <p className="text-xs text-gray-500 mt-2">Eller ange URL manuellt:</p>
              <Input
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
            </div>

            {/* Churches */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kyrkor *
              </label>
              <div className="space-y-2">
                {churchOptions.map((church) => (
                  <label key={church.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.churches.includes(church.value)}
                      onChange={() => handleChurchToggle(church.value)}
                      className="w-4 h-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                    />
                    <span className="text-sm">{church.label}</span>
                  </label>
                ))}
              </div>
              {formData.churches.length === 0 && (
                <p className="text-xs text-red-500 mt-1">Välj minst en kyrka</p>
              )}
            </div>

            {/* Event Date (Optional) */}
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Händelsedatum (Valfritt)
              </label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Om ett händelsedatum anges visas det på hemsidan och meddelandet döljs automatiskt efter datumet har passerat.
                Lämna tomt för tidlösa meddelanden som du styr manuellt.
              </p>
            </div>

            {/* Visibility */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">Synligt på hemsidan</label>
                <p className="text-xs text-gray-500">Visa meddelandet för besökare</p>
              </div>
              <Switch
                checked={formData.is_visible}
                onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                Avbryt
              </Button>
              <Button
                type="submit"
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 min-w-[120px]"
                disabled={formData.churches.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></span>
                    Sparar...
                  </span>
                ) : (
                  editingAnnouncement ? 'Uppdatera' : 'Skapa'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticker Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ticker-inställningar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max tecken i ticker
              </label>
              <Input
                type="number"
                min={20}
                max={200}
                value={tickerMaxChars}
                onChange={(e) => setTickerMaxChars(parseInt(e.target.value) || 80)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Titlar längre än detta antal tecken kommer att trunkeras i tickern.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
                Avbryt
              </Button>
              <Button
                onClick={handleSaveTickerSettings}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900"
              >
                Spara
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsManager;
